import { initializeApp } from 'firebase/app';
import {
	getAuth,
	createUserWithEmailAndPassword,
	signInWithEmailAndPassword,
	signInWithPopup,
	signInWithCustomToken,
	signInAnonymously,
	sendPasswordResetEmail,
	signOut,
	GoogleAuthProvider,
	FacebookAuthProvider,
	TwitterAuthProvider,
	EmailAuthProvider,
	getRedirectResult,
} from 'firebase/auth';
import {
	getFirestore,
	collection,
	doc,
	getDoc,
	getDocs,
	setDoc,
	addDoc,
	updateDoc,
	deleteDoc,
	query,
	where,
	orderBy,
	limit,
	startAfter,
	startAt,
	endBefore,
	endAt,
	onSnapshot,
	deleteField,
	serverTimestamp,
	arrayRemove,
	arrayUnion,
} from 'firebase/firestore';
import {
	getDatabase,
	ref,
	set,
	onValue,
	onDisconnect,
	serverTimestamp as rtdbServerTimestamp,
} from 'firebase/database';

import Swal from 'sweetalert2';
import I18n from '../../locales/i18n';

import config from './config';

import Server from '../../services/Server';

class Firebase {
	constructor() {
		const app = initializeApp(config);
		this.auth = getAuth(app);
		// Store the raw Firestore instance for internal use
		this._firestoreDb = getFirestore(app);
		this.realtimedatabase = getDatabase(app);
		
		// Create a wrapper for db with backwards-compatible methods
		const firestoreDb = this._firestoreDb;
		
		// Helper to create chainable query wrapper
		const createQueryWrapper = (q) => ({
			where: (field, op, value) => createQueryWrapper(query(q, where(field, op, value))),
			orderBy: (field, direction) => createQueryWrapper(query(q, orderBy(field, direction))),
			limit: (n) => createQueryWrapper(query(q, limit(n))),
			startAfter: (docSnapshot) => createQueryWrapper(query(q, startAfter(docSnapshot))),
			startAt: (docSnapshot) => createQueryWrapper(query(q, startAt(docSnapshot))),
			endBefore: (docSnapshot) => createQueryWrapper(query(q, endBefore(docSnapshot))),
			endAt: (docSnapshot) => createQueryWrapper(query(q, endAt(docSnapshot))),
			get: () => getDocs(q),
			onSnapshot: (callback, errorCallback) => onSnapshot(q, callback, errorCallback),
		});
		
		// Helper to create doc wrapper
		const createDocWrapper = (docRef) => ({
			get: () => getDoc(docRef),
			set: (data, options) => setDoc(docRef, data, options || {}),
			update: (data) => updateDoc(docRef, data),
			delete: () => deleteDoc(docRef),
			onSnapshot: (callback, errorCallback) => onSnapshot(docRef, callback, errorCallback),
			collection: (subCollectionName) => {
				const subCollectionRef = collection(firestoreDb, docRef.path, subCollectionName);
				return createCollectionWrapper(subCollectionRef);
			},
		});
		
		// Helper to create collection wrapper
		const createCollectionWrapper = (collectionRef) => ({
			doc: (docId) => {
				const docRef = doc(collectionRef, docId);
				return createDocWrapper(docRef);
			},
			add: (data) => addDoc(collectionRef, data),
			get: () => getDocs(collectionRef),
			onSnapshot: (callback, errorCallback) => onSnapshot(collectionRef, callback, errorCallback),
			where: (field, op, value) => createQueryWrapper(query(collectionRef, where(field, op, value))),
			orderBy: (field, direction) => createQueryWrapper(query(collectionRef, orderBy(field, direction))),
			limit: (n) => createQueryWrapper(query(collectionRef, limit(n))),
		});
		
		// Store wrappers as instance methods for use in other methods
		this._createCollectionWrapper = createCollectionWrapper;
		this._createQueryWrapper = createQueryWrapper;
		this._createDocWrapper = createDocWrapper;
		
		this.db = {
			// The raw Firestore instance
			_db: firestoreDb,
			// Backwards compatible collection method
			collection: (collectionName) => {
				const collectionRef = collection(firestoreDb, collectionName);
				return createCollectionWrapper(collectionRef);
			},
		};
		
		// Auth providers
		this.googleProvider = new GoogleAuthProvider();
		this.facebookProvider = new FacebookAuthProvider();
		this.twitterProvider = new TwitterAuthProvider();
		this.emailProvider = EmailAuthProvider;
		
		this.user = null;
		
		// Firestore utilities
		this.FieldValue = {
			serverTimestamp,
			delete: deleteField,
			arrayRemove,
			arrayUnion,
		};
		this.SERVER_TIMESTAMP = rtdbServerTimestamp();
		this.FIRESTORE_TIMESTAMP = serverTimestamp();
		this.arrayRemove = arrayRemove;
		this.arrayUnion = arrayUnion;

		// Handle redirect result
		getRedirectResult(this.auth)
			.then((result) => {})
			.catch((error) => {
				const { code = '', email = 'none@eventovirtual.co' } = error;
				if (code === 'auth/account-exists-with-different-credential') {
					Server.signInWithTokenByEmail({ email })
						.then((response) => {
							this.doSignInWithToken(response.data);
						})
						.catch(() => {
							Swal.fire('Opps!', I18n('ERRORS', error.code), 'error');
						});
				} else if (code) {
					Swal.fire('Opps!', I18n('ERRORS', error.code), 'error');
				}
			});
	}

	doCreateUserWithEmailAndPassword = (email, password) =>
		createUserWithEmailAndPassword(this.auth, email, password);

	doSignInWithEmailAndPassword = (email, password) =>
		signInWithEmailAndPassword(this.auth, email, password);

	doSignInWithGoogle = () => signInWithPopup(this.auth, this.googleProvider);

	doSignInWithFacebook = () => signInWithPopup(this.auth, this.facebookProvider);

	doSignInWithTwitter = () => signInWithPopup(this.auth, this.twitterProvider);

	doSignInWithToken = (token) => {
		let _token = '';
		for (var index in token) {
			_token += token[index];
		}
		return signInWithCustomToken(this.auth, _token);
	};

	doSignInAnonymous = () => signInAnonymously(this.auth);

	doSendPasswordResetEmail = (email) => sendPasswordResetEmail(this.auth, email);

	doSignOut = () => signOut(this.auth);

	getCredential = () => {
		const providerId = this.auth.currentUser.providerData[0].providerId;
		if (providerId === 'facebook.com') {
			return FacebookAuthProvider.credential(this.user.email);
		}
		return 'Holi';
	};

	setUser = (user) => (this.user = user);

	set = (collectionName, id, data) => {
		if (data === undefined) {
			return setDoc(doc(this._firestoreDb, collectionName, crypto.randomUUID()), id);
		}
		if ((id === null || id === undefined) && data !== undefined) {
			return addDoc(collection(this._firestoreDb, collectionName), data);
		}
		return setDoc(doc(this._firestoreDb, collectionName, id), data);
	};

	add = (collectionName, data, subCollection, data2) => {
		if (subCollection) {
			return addDoc(
				collection(this._firestoreDb, collectionName, data, subCollection),
				data2
			);
		}
		return addDoc(collection(this._firestoreDb, collectionName), data);
	};

	getCollection = (
		collectionName,
		condition_field,
		condition,
		condition_value,
		secondCondition,
		thirdCondition,
		fourthCondition
	) => {
		let q = collection(this._firestoreDb, collectionName);
		const conditions = [];

		if (condition_field) {
			conditions.push(where(condition_field, condition, condition_value));
		}
		if (secondCondition) {
			conditions.push(
				where(
					secondCondition.condition_field,
					secondCondition.condition,
					secondCondition.condition_value
				)
			);
		}
		if (thirdCondition) {
			conditions.push(
				where(
					thirdCondition.condition_field,
					thirdCondition.condition,
					thirdCondition.condition_value
				)
			);
		}
		if (fourthCondition) {
			conditions.push(
				where(
					fourthCondition.condition_field,
					fourthCondition.condition,
					fourthCondition.condition_value
				)
			);
		}

		if (conditions.length > 0) {
			q = query(q, ...conditions);
		}

		return getDocs(q);
	};

	getDoc = (collectionName, id, collection2, id2) => {
		let docRef;
		if (collection2 !== undefined) {
			docRef = doc(this._firestoreDb, collectionName, id, collection2, id2);
		} else {
			docRef = doc(this._firestoreDb, collectionName, id);
		}
		
		// Return a wrapper object with methods compatible with old Firebase API
		return {
			get: () => getDoc(docRef),
			set: (data, options) => setDoc(docRef, data, options || {}),
			update: (data) => updateDoc(docRef, data),
			delete: () => deleteDoc(docRef),
			onSnapshot: (callback, errorCallback) => onSnapshot(docRef, callback, errorCallback),
			// Also expose the raw reference
			ref: docRef,
		};
	};

	updateDoc = (docRefOrWrapper, values) => {
		// Handle both raw DocumentReference and our wrapper objects
		const actualRef = docRefOrWrapper?.ref || docRefOrWrapper;
		return updateDoc(actualRef, values);
	};

	deleteDoc = (collectionName, id) => {
		return deleteDoc(doc(this._firestoreDb, collectionName, id));
	};

	newRoom = (collectionName, id, group, data, data2, data3, data4) => {
		if (data2 === undefined) {
			return addDoc(collection(this._firestoreDb, collectionName, id, group), data);
		}

		if (data3 === undefined) {
			return updateDoc(doc(this._firestoreDb, collectionName, id, group, data), data2);
		}

		if (data4 === undefined) {
			return addDoc(
				collection(this._firestoreDb, collectionName, id, group, data, data2),
				data3
			);
		}

		return setDoc(
			doc(this._firestoreDb, collectionName, id, group, data, data2, data3),
			data4
		);
	};

	getRooms = (collectionName, id, group, orderByField, callback, data2, data3) => {
		if (data2 !== undefined) {
			const q = query(
				collection(this._firestoreDb, collectionName, id, group, orderByField, callback),
				orderBy(data2.field, data2.type)
			);
			return onSnapshot(q, data3);
		}

		const q = query(
			collection(this._firestoreDb, collectionName, id, group),
			orderBy(orderByField.field, orderByField.type)
		);
		return onSnapshot(q, callback);
	};

	getRoom = (collectionName, id, group, roomId) => {
		if (Array.isArray(id)) {
			const conditions = id.map((condition) =>
				where(
					condition.condition_field,
					condition.condition,
					condition.condition_value
				)
			);
			const q = query(collection(this._firestoreDb, collectionName), ...conditions);
			return onSnapshot(q, group);
		}
		if (roomId === undefined) {
			// Return a wrapper with chainable methods
			const collectionRef = collection(this._firestoreDb, collectionName, id, group);
			return this._createCollectionWrapper(collectionRef);
		}
		return doc(this._firestoreDb, collectionName, id, group, roomId);
	};

	deleteRoom = (collectionName, id, group, roomId) => {
		return deleteDoc(doc(this._firestoreDb, collectionName, id, group, roomId));
	};

	subCollection = {
		add: (collectionName, id, subcollection, field_id, field_value) => {
			return setDoc(
				doc(this._firestoreDb, collectionName, id, subcollection, field_id),
				field_value
			);
		},
		get: (collectionName, id, subcollection, field_id, field_id2, field_id3) => {
			if (field_id) {
				if (field_id2) {
					if (field_id3) {
					return getDoc(
						doc(
							this._firestoreDb,
							collectionName,
							id,
							subcollection,
							field_id,
							field_id2,
							field_id3
						)
					);
				}
				return getDocs(
					collection(
						this._firestoreDb,
						collectionName,
						id,
						subcollection,
						field_id,
						field_id2
					)
				);
				}
				return getDoc(doc(this._firestoreDb, collectionName, id, subcollection, field_id));
			}
			return getDocs(collection(this._firestoreDb, collectionName, id, subcollection));
		},
		update: (collectionName, id, subcollection, field_id, data) => {
			return updateDoc(
				doc(this._firestoreDb, collectionName, id, subcollection, field_id),
				data
			);
		},
		delete: (collectionName, id, subcollection, field_id) => {
			return deleteDoc(doc(this._firestoreDb, collectionName, id, subcollection, field_id));
		},
	};

	initPresence(room, onOnline, onOffline, customUid) {
		const uid = customUid || this.auth.currentUser.uid;

		const userStatusDatabaseRef = ref(this.realtimedatabase, `/${room}/${uid}`);
		const self = this;

		const connectedRef = ref(this.realtimedatabase, '.info/connected');
		onValue(connectedRef, (snapshot) => {
			if (snapshot.val() === false) {
				return;
			}

			onOnline.time = self.SERVER_TIMESTAMP;
			onOffline.time = self.SERVER_TIMESTAMP;

			onDisconnect(userStatusDatabaseRef)
				.set(onOffline)
				.then(() => {
					set(userStatusDatabaseRef, onOnline);
				});
		});
	}

	getCollectionBySocket(
		collectionName,
		collection_id,
		subcollection,
		doc_id,
		resolve
	) {
		if (typeof doc_id !== 'string') {
			if (typeof subcollection === 'function') {
				return onSnapshot(doc(this._firestoreDb, collectionName, collection_id), subcollection);
			}

			const q = query(
				collection(this._firestoreDb, collectionName, collection_id, subcollection),
				where(doc_id.condition_field, doc_id.condition, doc_id.condition_value)
			);
			return onSnapshot(q, resolve);
		}
		return onSnapshot(
			doc(this._firestoreDb, collectionName, collection_id, subcollection, doc_id),
			resolve
		);
	}

	deleteField(docRef, fields) {
		const _payload = {};
		fields.forEach((field) => {
			_payload[field] = deleteField();
		});
		return updateDoc(docRef, _payload);
	}

	updateById(collectionName, id, data) {
		return updateDoc(doc(this._firestoreDb, collectionName, id), data);
	}
}

export default Firebase;
